import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { isGameMaster, cn } from '../lib/utils';
import { PIN_TYPES, getPinColor, getPinIcon, getPinLabel } from '../lib/mapPins';
import { toast } from 'sonner';
import { MapViewer, TacticalGridOverlay, GridControlsPanel } from '../components/maps';
import { FogOverlay, FogBrushPreview } from '../components/maps/FogOverlay';
import { 
  Plus, 
  Map as MapIcon,
  MapPin,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  ZoomIn,
  ZoomOut,
  Move,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Home,
  Link2,
  ExternalLink,
  Navigation,
  Layers,
  Filter,
  Search,
  Info,
  MapPinned,
  Compass,
  Edit3,
  Hand,
  MousePointer2,
  Copy,
  Check,
  RefreshCw,
  MonitorPlay,
  Unlink,
  Cloud,
  CloudOff,
  Paintbrush,
  CircleDot,
  RotateCcw,
  ImageOff,
  Upload,
  Image as ImageIcon,
  Grid3X3
} from 'lucide-react';

const API_URL = '/api';

const MAP_TYPES = [
  { id: 'world', label: { fr: 'Monde', en: 'World' } },
  { id: 'region', label: { fr: 'Région', en: 'Region' } },
  { id: 'city', label: { fr: 'Ville', en: 'City' } },
  { id: 'dungeon', label: { fr: 'Donjon', en: 'Dungeon' } },
  { id: 'building', label: { fr: 'Bâtiment', en: 'Building' } }
];

export default function MapsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedMapId = searchParams.get('mapId');
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  // Data state
  const [maps, setMaps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [quests, setQuests] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation history for breadcrumbs
  const [mapHistory, setMapHistory] = useState([]);
  // Accordion: which map-type sections are open (all open by default)
  const [openSections, setOpenSections] = useState(() =>
    MAP_TYPES.reduce((acc, t) => ({ ...acc, [t.id]: true }), {})
  );
  const toggleSection = (typeId) =>
    setOpenSections(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  
  // UI state
  const [createMapOpen, setCreateMapOpen] = useState(false);
  const [editMapOpen, setEditMapOpen] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [createPinOpen, setCreatePinOpen] = useState(false);
  const [pinDetailOpen, setPinDetailOpen] = useState(false);
  const [selectedPin, setSelectedPin] = useState(null);
  const [hoveredPin, setHoveredPin] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);
  
  // Map controls
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    showUndiscovered: true,
    pinTypes: PIN_TYPES.map(p => p.id)
  });
  
  // Side panel
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  
  // Player View
  const [playerViewToken, setPlayerViewToken] = useState(null);
  const [playerViewDialogOpen, setPlayerViewDialogOpen] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [activePlayerMapId, setActivePlayerMapId] = useState(null);
  const [activePlayerMapName, setActivePlayerMapName] = useState('');
  const [playerViewDefaultImage, setPlayerViewDefaultImage] = useState(null);
  const [uploadingDefaultImage, setUploadingDefaultImage] = useState(false);
  
  // Fog of War
  const [fogMode, setFogMode] = useState(false);
  const [fogEnabled, setFogEnabled] = useState(false);
  const [fogSettings, setFogSettings] = useState({
    opacity: 0.85,
    color: '#1a1a2e',
    blur: 10
  });
  const [fogRevealedZones, setFogRevealedZones] = useState([]);
  const [fogBrushSize, setFogBrushSize] = useState(0.05); // 5% of map width
  const [fogBrushPosition, setFogBrushPosition] = useState({ x: null, y: null });
  const [fogDialogOpen, setFogDialogOpen] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const fogBrushBuffer = useRef([]);

  // Tactical Grid
  const [gridEnabled, setGridEnabled] = useState(false);
  const [gridSettings, setGridSettings] = useState({
    size: 50, color: '#ffffff', opacity: 0.3, type: 'square', offset_x: 0, offset_y: 0
  });
  const [gridPanelOpen, setGridPanelOpen] = useState(false);
  const gridSaveTimeoutRef = useRef(null);
  
  const mapContainerRef = useRef(null);
  const syncIntervalRef = useRef(null);
  
  // Form state
  const [newMap, setNewMap] = useState({
    name: '',
    image_url: '',
    map_type: 'world',
    visibility: 'players',
    parent_map_id: null,
    width: 1920,
    height: 1080,
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const createFileRef = useRef(null);
  const editFileRef = useRef(null);
  // Quick-create location from pin
  const [creatingLocationFromPin, setCreatingLocationFromPin] = useState(false);
  const [savingNewLocation, setSavingNewLocation] = useState(false);
  const [newLocationFromPin, setNewLocationFromPin] = useState({ name: '', location_type: 'poi', public_description: '' });
  
  const [newPin, setNewPin] = useState({
    title: '',
    pin_type: 'city',
    public_description: '',
    mj_notes: '',
    visibility: 'mj_only',
    discovered: false,
    linked_location_id: '',
    linked_map_id: '',
    linked_quests: [],
    linked_npcs: []
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  // Open edit map dialog
  const openEditMap = (map) => {
    setEditingMap({
      id: map.id,
      name: map.name,
      image_url: map.image_url,
      map_type: map.map_type,
      width: map.width,
      height: map.height,
      parent_map_id: map.parent_map_id,
      visibility: map.visibility || 'players'
    });
    setEditMapOpen(true);
  };

  // Handle edit map submit
  const handleEditMap = async (e) => {
    e.preventDefault();
    if (!editingMap) return;
    
    setCreating(true);
    try {
      await axios.patch(`${API_URL}/maps/${editingMap.id}`, {
        name: editingMap.name,
        image_url: editingMap.image_url,
        width: editingMap.width,
        height: editingMap.height,
        visibility: editingMap.visibility,
        parent_map_id: editingMap.parent_map_id
      });
      
      toast.success(language === 'fr' ? 'Carte modifiée !' : 'Map updated!');
      setEditMapOpen(false);
      setEditingMap(null);
      fetchData();
      
      // Update current map if it was edited
      if (currentMap?.id === editingMap.id) {
        setCurrentMap(prev => ({ ...prev, ...editingMap }));
      }
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mapsRes, locationsRes, questsRes, npcsRes] = await Promise.all([
        axios.get(`${API_URL}/campaigns/${campaignId}/maps`),
        axios.get(`${API_URL}/campaigns/${campaignId}/locations`),
        axios.get(`${API_URL}/campaigns/${campaignId}/quests`),
        axios.get(`${API_URL}/campaigns/${campaignId}/npcs`)
      ]);
      
      setMaps(mapsRes.data);
      setLocations(locationsRes.data);
      setQuests(questsRes.data);
      setNpcs(npcsRes.data);
      
      // Set first map as current if exists
      if (mapsRes.data.length > 0) {
        const rootMaps = mapsRes.data.filter(m => !m.parent_map_id);
        const requestedMap = mapsRes.data.find(m => m.id === requestedMapId);
        const initialMap = requestedMap || rootMaps[0] || mapsRes.data[0];
        setCurrentMap(initialMap);
        setMapHistory([initialMap]);
      }
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [campaignId, language, requestedMapId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch pins when current map changes
  useEffect(() => {
    if (currentMap) {
      fetchPins(currentMap.id);
      // Reset view on map change
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [currentMap]);

  const fetchPins = async (mapId) => {
    try {
      const response = await axios.get(`${API_URL}/maps/${mapId}/pins`);
      setPins(response.data);
    } catch (error) {
      console.error('Failed to fetch pins:', error);
    }
  };

  // Navigate to a linked map
  const navigateToMap = (mapId) => {
    const targetMap = maps.find(m => m.id === mapId);
    if (targetMap) {
      setMapHistory(prev => [...prev, targetMap]);
      setCurrentMap(targetMap);
      setPinDetailOpen(false);
      setSelectedPin(null);
    }
  };

  // Navigate back in history
  const navigateBack = () => {
    if (mapHistory.length > 1) {
      const newHistory = [...mapHistory];
      newHistory.pop();
      setMapHistory(newHistory);
      setCurrentMap(newHistory[newHistory.length - 1]);
    }
  };

  // Navigate to root
  const navigateToRoot = () => {
    if (mapHistory.length > 0) {
      setMapHistory([mapHistory[0]]);
      setCurrentMap(mapHistory[0]);
    }
  };

  const handleUploadMapImage = async (file, target = 'create') => {
    if (!file) return;
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 50 Mo)' : 'File too large (max 50 MB)');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error(language === 'fr' ? 'Format non supporté (JPG, PNG, WebP)' : 'Unsupported format (JPG, PNG, WebP)');
      return;
    }
    const setter = target === 'create' ? setUploadingImage : setUploadingEditImage;
    setter(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API_URL}/campaigns/${campaignId}/maps/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (target === 'create') {
        setNewMap(prev => ({ ...prev, image_url: res.data.image_url, width: res.data.width, height: res.data.height }));
      } else {
        setEditingMap(prev => ({ ...prev, image_url: res.data.image_url, width: res.data.width, height: res.data.height }));
      }
      toast.success(language === 'fr' ? 'Image importée' : 'Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur d\'import' : 'Upload error'));
    } finally {
      setter(false);
    }
  };

  const handleCreateMap = async (e) => {
    e.preventDefault();
    if (!newMap.name.trim() || !newMap.image_url.trim()) {
      toast.error(t('error_required', language));
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/maps`, newMap);
      setMaps(prev => [...prev, response.data]);
      setCurrentMap(response.data);
      setMapHistory([response.data]);
      setCreateMapOpen(false);
      setNewMap({
        name: '', image_url: '', map_type: 'world', visibility: 'players',
        parent_map_id: null, width: 1920, height: 1080,
      });
      toast.success(t('success_saved', language));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  const handleMapClick = (e) => {
    // Only allow creating pins in edit mode
    if (!isMJ || !editMode) return;
    
    // Get click position relative to the map image
    const mapContainer = e.target.closest('.map-pins-container');
    if (!mapContainer) return;
    
    const img = mapContainer.querySelector('img');
    if (!img) return;
    
    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Validate position is within map bounds
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    
    setClickPosition({ x, y });
    setCreatePinOpen(true);
  };

  // Handle pin position change (drag & drop)
  const handlePinMove = async (pinId, newX, newY) => {
    try {
      await axios.patch(`${API_URL}/pins/${pinId}`, { x: newX, y: newY });
      setPins(prev => prev.map(p => p.id === pinId ? { ...p, x: newX, y: newY } : p));
      toast.success(language === 'fr' ? 'Pin déplacé' : 'Pin moved');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du déplacement' : 'Move failed');
    }
  };

  const handleCreatePin = async (e) => {
    e.preventDefault();
    if (!newPin.title.trim() || !clickPosition) {
      toast.error(t('error_required', language));
      return;
    }

    setCreating(true);
    try {
      const pinData = {
        map_id: currentMap.id,
        x: clickPosition.x,
        y: clickPosition.y,
        title: newPin.title,
        pin_type: newPin.pin_type,
        public_description: newPin.public_description,
        mj_notes: newPin.mj_notes,
        visibility: newPin.visibility,
        discovered: newPin.discovered,
        linked_locations: newPin.linked_location_id ? [newPin.linked_location_id] : [],
        linked_map_id: newPin.linked_map_id || null,
        linked_quests: newPin.linked_quests,
        linked_npcs: newPin.linked_npcs
      };
      
      const response = await axios.post(`${API_URL}/maps/${currentMap.id}/pins`, pinData);
      setPins(prev => [...prev, response.data]);
      setCreatePinOpen(false);
      setClickPosition(null);
      setNewPin({
        title: '',
        pin_type: 'city',
        public_description: '',
        mj_notes: '',
        visibility: 'mj_only',
        discovered: false,
        linked_location_id: '',
        linked_map_id: '',
        linked_quests: [],
        linked_npcs: []
      });
      toast.success(t('success_saved', language));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  const handlePinClick = (pin, e) => {
    e.stopPropagation();
    setSelectedPin(pin);
    setPinDetailOpen(true);
  };

  const handlePinDoubleClick = (pin, e) => {
    e.stopPropagation();
    // If pin has linked map, navigate to it
    if (pin.linked_map_id) {
      navigateToMap(pin.linked_map_id);
    }
  };

  const handleUpdatePin = async () => {
    if (!selectedPin) return;
    
    try {
      const response = await axios.patch(`${API_URL}/pins/${selectedPin.id}`, {
        title: selectedPin.title,
        public_description: selectedPin.public_description,
        mj_notes: selectedPin.mj_notes,
        visibility: selectedPin.visibility,
        discovered: selectedPin.discovered,
        linked_locations: selectedPin.linked_locations,
        linked_map_id: selectedPin.linked_map_id,
        linked_quests: selectedPin.linked_quests,
        linked_npcs: selectedPin.linked_npcs
      });
      setPins(prev => prev.map(p => p.id === selectedPin.id ? response.data : p));
      toast.success(t('success_saved', language));
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  const handleCreateLocationFromPin = async () => {
    if (!newLocationFromPin.name.trim() || !selectedPin) return;
    setSavingNewLocation(true);
    try {
      // Create one canonical encyclopedia entity. The API exposes its location
      // projection to the map with the same identity.
      const loreRes = await axios.post(`${API_URL}/campaigns/${campaignId}/lore`, {
        title: newLocationFromPin.name.trim(),
        article_type: 'location',
        summary: newLocationFromPin.public_description,
        content: newLocationFromPin.public_description,
        visibility: 'players',
        revelation_level: 'known',
        tags: [newLocationFromPin.location_type].filter(Boolean),
      });
      const locationId = loreRes.data.entity_id || loreRes.data.id;
      const locationRes = await axios.get(`${API_URL}/locations/${locationId}`);
      const newLoc = locationRes.data;

      // Add to local locations list
      setLocations(prev => [...prev, newLoc]);
      // Auto-link the new location to the current pin
      setSelectedPin(prev => ({ ...prev, linked_locations: [newLoc.id] }));
      // Reset form
      setCreatingLocationFromPin(false);
      setNewLocationFromPin({ name: '', location_type: 'poi', public_description: '' });
      toast.success(language === 'fr' ? `Lieu "${newLoc.name}" créé et ajouté à l'encyclopédie` : `Location "${newLoc.name}" created & added to encyclopedia`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSavingNewLocation(false);
    }
  };

  const handleRevealPin = async () => {
    if (!selectedPin) return;
    
    try {
      await axios.post(`${API_URL}/pins/${selectedPin.id}/reveal`);
      setPins(prev => prev.map(p => p.id === selectedPin.id ? { ...p, discovered: true, visibility: 'players' } : p));
      setSelectedPin(prev => ({ ...prev, discovered: true, visibility: 'players' }));
      toast.success(language === 'fr' ? 'Pin révélé aux joueurs' : 'Pin revealed to players');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  const handleDeletePin = async () => {
    if (!selectedPin) return;
    if (!window.confirm(language === 'fr' ? 'Supprimer ce pin ?' : 'Delete this pin?')) return;
    
    try {
      await axios.delete(`${API_URL}/pins/${selectedPin.id}`);
      setPins(prev => prev.filter(p => p.id !== selectedPin.id));
      setPinDetailOpen(false);
      setSelectedPin(null);
      toast.success(t('success_deleted', language));
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  const handleDeleteMap = async () => {
    if (!currentMap) return;
    if (!window.confirm(language === 'fr' ? 'Supprimer cette carte et tous ses pins ?' : 'Delete this map and all its pins?')) return;
    
    try {
      await axios.delete(`${API_URL}/maps/${currentMap.id}`);
      setMaps(prev => prev.filter(m => m.id !== currentMap.id));
      const remaining = maps.filter(m => m.id !== currentMap.id);
      setCurrentMap(remaining.length > 0 ? remaining[0] : null);
      setMapHistory(remaining.length > 0 ? [remaining[0]] : []);
      setPins([]);
      toast.success(t('success_deleted', language));
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // ========== PLAYER VIEW FUNCTIONS ==========
  
  // Fetch player view token
  const fetchPlayerViewToken = async () => {
    if (!currentMap) return;
    try {
      const response = await axios.get(`${API_URL}/maps/${currentMap.id}/player-view/token`);
      setPlayerViewToken(response.data.token);
      setActivePlayerMapId(response.data.active_map_id || null);
      setActivePlayerMapName(response.data.active_map_name || '');
      setPlayerViewDefaultImage(response.data.player_view_default_image || null);
    } catch (error) {
      setPlayerViewToken(null);
      setActivePlayerMapId(null);
      setActivePlayerMapName('');
    }
  };

  // Generate initial player view token
  const generatePlayerViewToken = async () => {
    if (!currentMap) return;
    try {
      const response = await axios.post(`${API_URL}/maps/${currentMap.id}/player-view/token`);
      setPlayerViewToken(response.data.token);
      setActivePlayerMapId(currentMap.id);
      setActivePlayerMapName(currentMap.name);
      toast.success(language === 'fr' ? 'Lien généré !' : 'Link generated!');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Push currently opened map to existing player view link
  const setCurrentMapForPlayerView = async () => {
    if (!currentMap || !playerViewToken) return;
    try {
      const response = await axios.post(`${API_URL}/maps/${currentMap.id}/player-view/token`);
      setPlayerViewToken(response.data.token);
      setActivePlayerMapId(currentMap.id);
      setActivePlayerMapName(currentMap.name);
      toast.success(language === 'fr' ? 'Vue joueur mise à jour sur cette carte' : 'Player view updated to this map');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Copy player view URL
  const copyPlayerViewUrl = () => {
    if (!playerViewToken) return;
    const url = `${window.location.origin}/player-view/map/${playerViewToken}`;
    navigator.clipboard.writeText(url);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
    toast.success(language === 'fr' ? 'Lien copié !' : 'Link copied!');
  };

  // Open player view in new window
  const openPlayerView = () => {
    if (!playerViewToken) return;
    const url = `${window.location.origin}/player-view/map/${playerViewToken}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Update sync state
  const updateSyncState = useCallback(async () => {
    if (!currentMap || !syncEnabled) return;
    try {
      await axios.post(`${API_URL}/maps/${currentMap.id}/player-view/sync`, {
        enabled: true,
        map_id: currentMap.id,
        pan_x: pan.x,
        pan_y: pan.y,
        zoom: zoom,
        focused_pin_id: selectedPin?.id || null
      });
    } catch (error) {
      console.error('Failed to update sync state');
    }
  }, [currentMap, syncEnabled, pan, zoom, selectedPin]);

  // Toggle sync
  const toggleSync = async () => {
    const newSyncState = !syncEnabled;
    setSyncEnabled(newSyncState);
    
    if (!currentMap) return;
    
    try {
      await axios.post(`${API_URL}/maps/${currentMap.id}/player-view/sync`, {
        enabled: newSyncState,
        map_id: currentMap.id,
        pan_x: pan.x,
        pan_y: pan.y,
        zoom: zoom,
        focused_pin_id: selectedPin?.id || null
      });
      
      if (newSyncState) {
        toast.success(language === 'fr' ? 'Synchronisation activée' : 'Sync enabled');
      } else {
        toast.info(language === 'fr' ? 'Synchronisation désactivée' : 'Sync disabled');
      }
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Clear player view display
  const clearPlayerViewDisplay = async () => {
    if (!campaignId) return;
    try {
      await axios.post(`${API_URL}/campaigns/${campaignId}/player-view/map/clear`);
      setActivePlayerMapId(null);
      setActivePlayerMapName('');
      setSyncEnabled(false);
      toast.success(language === 'fr' ? 'Affichage joueur retiré' : 'Player display cleared');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Upload default image for player view idle screen
  const uploadPlayerViewDefaultImage = async (file) => {
    if (!campaignId || !file) return;
    setUploadingDefaultImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API_URL}/campaigns/${campaignId}/player-view/default-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPlayerViewDefaultImage(response.data.image_url);
      toast.success(language === 'fr' ? 'Image par défaut enregistrée' : 'Default image saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setUploadingDefaultImage(false);
    }
  };

  // Remove default image
  const removePlayerViewDefaultImage = async () => {
    if (!campaignId) return;
    try {
      await axios.delete(`${API_URL}/campaigns/${campaignId}/player-view/default-image`);
      setPlayerViewDefaultImage(null);
      toast.success(language === 'fr' ? 'Image par défaut supprimée' : 'Default image removed');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Sync effect - push state to server when enabled
  useEffect(() => {
    if (syncEnabled && currentMap) {
      // Debounced sync
      const timeoutId = setTimeout(() => {
        updateSyncState();
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [syncEnabled, currentMap, pan, zoom, selectedPin, updateSyncState]);

  // Fetch token when map changes
  useEffect(() => {
    if (currentMap && isMJ) {
      fetchPlayerViewToken();
      fetchFogData();
      fetchGridData();
    }
  }, [currentMap, isMJ]);

  // ========== TACTICAL GRID FUNCTIONS ==========

  const fetchGridData = async () => {
    if (!currentMap) return;
    try {
      const response = await axios.get(`${API_URL}/maps/${currentMap.id}/grid`);
      setGridEnabled(response.data.enabled);
      setGridSettings(response.data.settings || { size: 50, color: '#ffffff', opacity: 0.3, type: 'square', offset_x: 0, offset_y: 0 });
    } catch (error) {
      console.error('Error fetching grid data');
    }
  };

  const toggleGrid = async () => {
    if (!currentMap) return;
    try {
      const newState = !gridEnabled;
      await axios.post(`${API_URL}/maps/${currentMap.id}/grid/toggle?enabled=${newState}`);
      setGridEnabled(newState);
      toast.success(language === 'fr'
        ? `Grille ${newState ? 'activée' : 'désactivée'}`
        : `Grid ${newState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur grille' : 'Grid error');
    }
  };

  const handleGridSettingsChange = (newSettings) => {
    setGridSettings(newSettings);
    // Debounce the API call
    if (gridSaveTimeoutRef.current) clearTimeout(gridSaveTimeoutRef.current);
    gridSaveTimeoutRef.current = setTimeout(async () => {
      if (!currentMap) return;
      try {
        await axios.post(`${API_URL}/maps/${currentMap.id}/grid/settings`, newSettings);
      } catch (error) {
        console.error('Error saving grid settings');
      }
    }, 300);
  };

  // ========== FOG OF WAR FUNCTIONS ==========
  
  // Fetch fog data when map changes
  const fetchFogData = async () => {
    if (!currentMap) return;
    try {
      const response = await axios.get(`${API_URL}/maps/${currentMap.id}/fog`);
      setFogEnabled(response.data.enabled);
      setFogSettings(response.data.settings || { opacity: 0.85, color: '#1a1a2e', blur: 10 });
      setFogRevealedZones(response.data.revealed_zones || []);
    } catch (error) {
      console.error('Error fetching fog data');
    }
  };

  // Toggle fog of war
  const toggleFog = async () => {
    if (!currentMap) return;
    try {
      const newState = !fogEnabled;
      await axios.post(`${API_URL}/maps/${currentMap.id}/fog/toggle?enabled=${newState}`);
      setFogEnabled(newState);
      toast.success(language === 'fr' 
        ? `Brouillard ${newState ? 'activé' : 'désactivé'}`
        : `Fog ${newState ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Reveal fog zone
  const revealFogZone = async (x, y, radius = fogBrushSize) => {
    if (!currentMap || !fogMode) return;
    try {
      const response = await axios.post(`${API_URL}/maps/${currentMap.id}/fog/reveal`, {
        x, y, radius, shape: 'circle'
      });
      setFogRevealedZones(prev => [...prev, response.data.zone]);
    } catch (error) {
      console.error('Error revealing zone');
    }
  };

  // Reveal multiple zones (for brush strokes)
  const revealMultipleZones = async (zones) => {
    if (!currentMap || zones.length === 0) return;
    try {
      const response = await axios.post(`${API_URL}/maps/${currentMap.id}/fog/reveal-multiple`, zones);
      setFogRevealedZones(prev => [...prev, ...response.data.zones]);
    } catch (error) {
      console.error('Error revealing zones');
    }
  };

  // Reset fog (cover entire map)
  const resetFog = async () => {
    if (!currentMap) return;
    if (!window.confirm(language === 'fr' 
      ? 'Réinitialiser le brouillard ? Toute la carte sera recouverte.'
      : 'Reset fog? The entire map will be covered.'
    )) return;
    
    try {
      await axios.post(`${API_URL}/maps/${currentMap.id}/fog/reset`);
      setFogRevealedZones([]);
      toast.success(language === 'fr' ? 'Brouillard réinitialisé' : 'Fog reset');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Reveal entire map
  const revealAllFog = async () => {
    if (!currentMap) return;
    try {
      await axios.post(`${API_URL}/maps/${currentMap.id}/fog/reveal-all`);
      setFogRevealedZones([{ id: 'full', x: 0.5, y: 0.5, radius: 1.5, shape: 'circle' }]);
      toast.success(language === 'fr' ? 'Carte entièrement révélée' : 'Entire map revealed');
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  // Handle fog brush mouse down
  const handleFogMouseDown = (e) => {
    if (!fogMode || !currentMap) return;
    setIsRevealing(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Add to buffer
    fogBrushBuffer.current = [{ x, y, radius: fogBrushSize, shape: 'circle' }];
    
    // Immediately show locally
    setFogRevealedZones(prev => [...prev, { 
      id: `temp-${Date.now()}`, 
      x, y, 
      radius: fogBrushSize, 
      shape: 'circle' 
    }]);
  };

  // Handle fog brush mouse move
  const handleFogMouseMove = (e) => {
    if (!fogMode || !currentMap) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    setFogBrushPosition({ x, y });
    
    if (isRevealing) {
      // Add to buffer (with spacing to avoid too many zones)
      const lastZone = fogBrushBuffer.current[fogBrushBuffer.current.length - 1];
      if (!lastZone || Math.hypot(x - lastZone.x, y - lastZone.y) > fogBrushSize * 0.3) {
        fogBrushBuffer.current.push({ x, y, radius: fogBrushSize, shape: 'circle' });
        
        // Immediately show locally
        setFogRevealedZones(prev => [...prev, { 
          id: `temp-${Date.now()}`, 
          x, y, 
          radius: fogBrushSize, 
          shape: 'circle' 
        }]);
      }
    }
  };

  // Handle fog brush mouse up
  const handleFogMouseUp = async () => {
    if (!isRevealing || fogBrushBuffer.current.length === 0) {
      setIsRevealing(false);
      return;
    }
    
    setIsRevealing(false);
    
    // Send all buffered zones to server
    await revealMultipleZones(fogBrushBuffer.current);
    fogBrushBuffer.current = [];
    
    // Refresh fog data to get proper IDs
    await fetchFogData();
  };

  // Handle fog brush mouse leave
  const handleFogMouseLeave = () => {
    setFogBrushPosition({ x: null, y: null });
    if (isRevealing) {
      handleFogMouseUp();
    }
  };

  // Get linked location details
  const getLinkedLocation = (locationId) => locations.find(l => l.id === locationId);
  const getLinkedQuest = (questId) => quests.find(q => q.id === questId);
  const getLinkedNpc = (npcId) => npcs.find(n => n.id === npcId);
  const getLinkedMap = (mapId) => maps.find(m => m.id === mapId);

  // Get child maps (maps that have this map as parent)
  const getChildMaps = (mapId) => maps.filter(m => m.parent_map_id === mapId);

  // Filter pins
  const filteredPins = pins.filter(pin => {
    if (!isMJ && !pin.discovered) return false;
    if (!filters.showUndiscovered && !pin.discovered && isMJ) return false;
    if (!filters.pinTypes.includes(pin.pin_type)) return false;
    return true;
  });

  return (
    <MainLayout fluid>
      <div className="flex h-[calc(100vh-4rem)]" data-testid="maps-page">
        {/* Side Panel */}
        <div className={cn(
          "border-r border-border bg-card transition-all duration-300 flex flex-col",
          sidePanelOpen ? "w-72" : "w-0 overflow-hidden"
        )}>
          {sidePanelOpen && (
            <>
              {/* Map List Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-cinzel font-bold text-lg">
                    {language === 'fr' ? 'Cartes' : 'Maps'}
                  </h2>
                  {isMJ && (
                    <Button size="icon" variant="ghost" onClick={() => setCreateMapOpen(true)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {/* Breadcrumb Navigation */}
                {mapHistory.length > 1 && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2 flex-wrap">
                    <Button variant="ghost" size="sm" className="h-6 px-1" onClick={navigateToRoot}>
                      <Home className="h-3 w-3" />
                    </Button>
                    {mapHistory.map((map, idx) => (
                      <React.Fragment key={map.id}>
                        <ChevronRight className="h-3 w-3" />
                        <span 
                          className={cn(
                            "cursor-pointer hover:text-foreground truncate max-w-[80px]",
                            idx === mapHistory.length - 1 && "text-foreground font-medium"
                          )}
                          onClick={() => {
                            const newHistory = mapHistory.slice(0, idx + 1);
                            setMapHistory(newHistory);
                            setCurrentMap(map);
                          }}
                        >
                          {map.name}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Maps List — grouped by type, accordion */}
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-0.5">
                  {maps.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {language === 'fr' ? 'Aucune carte' : 'No maps'}
                    </p>
                  ) : (
                    MAP_TYPES.map(type => {
                      const typeMaps = maps.filter(m => (m.map_type || 'world') === type.id && !m.parent_map_id);
                      if (typeMaps.length === 0) return null;
                      const isOpen = openSections[type.id];
                      return (
                        <div key={type.id}>
                          {/* Accordion header */}
                          <button
                            type="button"
                            onClick={() => toggleSection(type.id)}
                            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors"
                          >
                            <ChevronDown className={cn(
                              "h-3 w-3 transition-transform duration-200",
                              !isOpen && "-rotate-90"
                            )} />
                            <span className="uppercase tracking-wider text-[10px]">{type.label[language]}</span>
                            <Badge variant="secondary" className="ml-auto h-4 text-[10px] px-1.5">
                              {typeMaps.length}
                            </Badge>
                          </button>
                          {/* Accordion body */}
                          {isOpen && (
                            <div className="ml-1 pl-2 border-l border-border/50 space-y-0.5 mb-1">
                              {typeMaps.map(map => (
                                <MapTreeItem
                                  key={map.id}
                                  map={map}
                                  maps={maps}
                                  currentMap={currentMap}
                                  language={language}
                                  isMJ={isMJ}
                                  onSelect={(m) => {
                                    setCurrentMap(m);
                                    setMapHistory([m]);
                                  }}
                                  onEdit={isMJ ? openEditMap : null}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
              
              {/* Pins on Current Map */}
              {currentMap && pins.length > 0 && (
                <div className="border-t border-border">
                  <div className="p-3">
                    <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <MapPinned className="h-4 w-4" />
                      {language === 'fr' ? 'Points d\'intérêt' : 'Points of Interest'}
                      <Badge variant="secondary" className="ml-auto">{filteredPins.length}</Badge>
                    </h3>
                  </div>
                  <ScrollArea className="h-40">
                    <div className="px-2 pb-2 space-y-1">
                      {filteredPins.map(pin => {
                        const Icon = getPinIcon(pin.pin_type);
                        const color = getPinColor(pin.pin_type);
                        return (
                          <div
                            key={pin.id}
                            className={cn(
                              "flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-secondary/50 text-sm",
                              selectedPin?.id === pin.id && "bg-secondary"
                            )}
                            onClick={() => { setSelectedPin(pin); setPinDetailOpen(true); }}
                          >
                            <div className="p-1 rounded" style={{ backgroundColor: color }}>
                              <Icon className="h-3 w-3 text-white" />
                            </div>
                            <span className="truncate flex-1">{pin.title}</span>
                            {!pin.discovered && isMJ && (
                              <EyeOff className="h-3 w-3 text-muted-foreground" />
                            )}
                            {pin.linked_map_id && (
                              <Layers className="h-3 w-3 text-primary" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between p-2 border-b border-border bg-card/50">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSidePanelOpen(!sidePanelOpen)}
              >
                {sidePanelOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
              
              {mapHistory.length > 1 && (
                <Button variant="ghost" size="sm" onClick={navigateBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {language === 'fr' ? 'Retour' : 'Back'}
                </Button>
              )}
              
              <div className="h-4 w-px bg-border" />
              
              <Button variant="outline" size="icon" onClick={() => setZoom(z => Math.min(z + 0.25, 3))}>
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>
                <Move className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">{Math.round(zoom * 100)}%</span>
              
              {/* Edit Mode Toggle (MJ only) */}
              {isMJ && currentMap && (
                <>
                  <div className="h-4 w-px bg-border ml-2" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={editMode ? "default" : "outline"}
                          size="sm"
                          onClick={() => setEditMode(!editMode)}
                          className={cn(
                            "gap-2",
                            editMode && "bg-orange-500 hover:bg-orange-600"
                          )}
                        >
                          {editMode ? (
                            <>
                              <Edit3 className="h-4 w-4" />
                              {language === 'fr' ? 'Mode Édition' : 'Edit Mode'}
                            </>
                          ) : (
                            <>
                              <Hand className="h-4 w-4" />
                              {language === 'fr' ? 'Navigation' : 'Navigate'}
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {editMode 
                          ? (language === 'fr' 
                              ? 'Clic = nouveau pin • Glisser pin = déplacer' 
                              : 'Click = new pin • Drag pin = move')
                          : (language === 'fr' 
                              ? 'Glisser = déplacer la carte • Clic pin = info' 
                              : 'Drag = pan map • Click pin = info')
                        }
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {isMJ && (
                <>
                  {currentMap && ['world', 'region', 'city'].includes(currentMap.map_type || 'world') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 border-blue-500/30 bg-blue-500/5 text-blue-700 hover:bg-blue-500/10 dark:text-blue-300"
                      onClick={() => navigate(`/campaigns/${campaignId}/maps/${currentMap.id}/travel-setup`)}
                      data-testid="travel-map-setup-button"
                    >
                      <Navigation className="h-4 w-4" />
                      {language === 'fr' ? 'Paramétrage Voyage' : 'Travel setup'}
                    </Button>
                  )}

                  {/* Player View Button */}
                  {currentMap && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPlayerViewDialogOpen(true)}
                        className="gap-2"
                        data-testid="player-view-btn"
                      >
                        <MonitorPlay className="h-4 w-4" />
                        {language === 'fr' ? 'Vue Joueur' : 'Player View'}
                      </Button>
                      {playerViewToken && activePlayerMapId && (
                        <Badge variant={activePlayerMapId === currentMap.id ? 'default' : 'secondary'}>
                          {language === 'fr' ? 'Affichée:' : 'Showing:'} {activePlayerMapName || activePlayerMapId}
                        </Badge>
                      )}
                    </>
                  )}

                  {/* Fog of War Button */}
                  {currentMap && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant={fogMode ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (!fogMode) {
                                setFogMode(true);
                                setEditMode(false); // Disable edit mode when entering fog mode
                              } else {
                                setFogMode(false);
                              }
                            }}
                            className={cn(
                              "gap-2",
                              fogMode && "bg-purple-600 hover:bg-purple-700"
                            )}
                            data-testid="fog-mode-btn"
                          >
                            {fogEnabled ? <Cloud className="h-4 w-4" /> : <CloudOff className="h-4 w-4" />}
                            {language === 'fr' ? 'Brouillard' : 'Fog'}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {language === 'fr' 
                            ? 'Brouillard de guerre - révélez progressivement la carte'
                            : 'Fog of war - progressively reveal the map'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {/* Tactical Grid Button */}
                  {currentMap && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={gridPanelOpen ? "default" : "outline"}
                            size="sm"
                            onClick={() => setGridPanelOpen(!gridPanelOpen)}
                            className={cn(
                              "gap-2",
                              gridPanelOpen && "bg-blue-600 hover:bg-blue-700"
                            )}
                          >
                            <Grid3X3 className="h-4 w-4" />
                            {language === 'fr' ? 'Grille' : 'Grid'}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {language === 'fr'
                            ? 'Grille tactique pour combat avec figurines'
                            : 'Tactical grid for miniature combat'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}

                  {/* Sync indicator */}
                  {syncEnabled && (
                    <Badge variant="secondary" className="gap-1 bg-green-500/20 text-green-400">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Sync
                    </Badge>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFilterOpen(!filterOpen)}
                    className={filterOpen ? 'bg-secondary' : ''}
                  >
                    <Filter className="h-4 w-4 mr-1" />
                    {language === 'fr' ? 'Filtres' : 'Filters'}
                  </Button>
                  
                  {currentMap && (
                    <Button variant="ghost" size="icon" onClick={handleDeleteMap} className="text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Filter Panel */}
          {filterOpen && isMJ && (
            <div className="p-3 border-b border-border bg-secondary/30">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={filters.showUndiscovered}
                    onCheckedChange={(v) => setFilters(prev => ({ ...prev, showUndiscovered: v }))}
                  />
                  <Label className="text-sm">
                    {language === 'fr' ? 'Afficher non découverts' : 'Show undiscovered'}
                  </Label>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {PIN_TYPES.map(type => {
                    const Icon = type.icon;
                    const isActive = filters.pinTypes.includes(type.id);
                    return (
                      <Button
                        key={type.id}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        className="h-7 px-2"
                        style={isActive ? { backgroundColor: type.color } : {}}
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            pinTypes: isActive 
                              ? prev.pinTypes.filter(t => t !== type.id)
                              : [...prev.pinTypes, type.id]
                          }));
                        }}
                      >
                        <Icon className="h-3 w-3" />
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Fog of War Control Panel */}
          {fogMode && isMJ && currentMap && (
            <div className="p-3 border-b border-border bg-purple-500/10">
              <div className="flex flex-wrap items-center gap-4">
                {/* Enable/Disable Fog */}
                <div className="flex items-center gap-2">
                  <Switch
                    checked={fogEnabled}
                    onCheckedChange={toggleFog}
                  />
                  <Label className="text-sm font-medium">
                    {language === 'fr' ? 'Brouillard actif' : 'Fog active'}
                  </Label>
                </div>

                <div className="h-4 w-px bg-border" />

                {/* Brush Size */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm">
                    <Paintbrush className="h-4 w-4 inline mr-1" />
                    {language === 'fr' ? 'Pinceau' : 'Brush'}
                  </Label>
                  <div className="flex items-center gap-1">
                    <Button
                      variant={fogBrushSize === 0.03 ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setFogBrushSize(0.03)}
                    >
                      S
                    </Button>
                    <Button
                      variant={fogBrushSize === 0.05 ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setFogBrushSize(0.05)}
                    >
                      M
                    </Button>
                    <Button
                      variant={fogBrushSize === 0.08 ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setFogBrushSize(0.08)}
                    >
                      L
                    </Button>
                    <Button
                      variant={fogBrushSize === 0.12 ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => setFogBrushSize(0.12)}
                    >
                      XL
                    </Button>
                  </div>
                </div>

                <div className="h-4 w-px bg-border" />

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={revealAllFog}
                          className="gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          {language === 'fr' ? 'Tout révéler' : 'Reveal all'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {language === 'fr' ? 'Révèle toute la carte' : 'Reveals the entire map'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetFog}
                          className="gap-1 text-destructive"
                        >
                          <RotateCcw className="h-4 w-4" />
                          {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {language === 'fr' ? 'Recouvre toute la carte' : 'Cover the entire map'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="ml-auto text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Cliquez et glissez sur la carte pour révéler des zones'
                    : 'Click and drag on the map to reveal areas'}
                </div>
              </div>
            </div>
          )}

          {/* Tactical Grid Control Panel */}
          {gridPanelOpen && isMJ && currentMap && (
            <GridControlsPanel
              gridEnabled={gridEnabled}
              gridSettings={gridSettings}
              onToggle={toggleGrid}
              onSettingsChange={handleGridSettingsChange}
              language={language}
            />
          )}

          {/* Map Viewer */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !currentMap ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MapIcon className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                <h3 className="font-cinzel text-xl font-bold mb-2">
                  {language === 'fr' ? 'Aucune carte' : 'No maps'}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  {language === 'fr'
                    ? 'Créez une carte pour commencer à explorer votre monde'
                    : 'Create a map to start exploring your world'}
                </p>
                {isMJ && (
                  <Button onClick={() => setCreateMapOpen(true)} className="fantasy-button">
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Nouvelle carte' : 'New map'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div 
              className={cn(
                "flex-1 flex flex-col relative",
                fogMode && "cursor-crosshair"
              )}
              onMouseDown={fogMode ? handleFogMouseDown : undefined}
              onMouseMove={fogMode ? handleFogMouseMove : undefined}
              onMouseUp={fogMode ? handleFogMouseUp : undefined}
              onMouseLeave={fogMode ? handleFogMouseLeave : undefined}
            >
              <MapViewer
                currentMap={currentMap}
                filteredPins={filteredPins}
                zoom={zoom}
                pan={pan}
                isDragging={isDragging}
                editMode={editMode && !fogMode}
                isMJ={isMJ}
                language={language}
                loading={false}
                getPinIcon={getPinIcon}
                getPinColor={getPinColor}
                onPanChange={fogMode ? () => {} : setPan}
                onZoomChange={fogMode ? () => {} : setZoom}
                onMapClick={fogMode ? () => {} : handleMapClick}
                onPinClick={fogMode ? () => {} : handlePinClick}
                onPinDoubleClick={fogMode ? () => {} : handlePinDoubleClick}
                onPinMove={fogMode ? () => {} : handlePinMove}
                onNavigateToLinkedMap={fogMode ? () => {} : navigateToMap}
              />
              
              {/* Tactical Grid Overlay (MJ preview — visible only when sync/controlling player view) */}
              {gridEnabled && syncEnabled && isMJ && currentMap && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center'
                  }}
                >
                  <TacticalGridOverlay
                    width={currentMap.width || 1920}
                    height={currentMap.height || 1080}
                    enabled={gridEnabled}
                    settings={gridSettings}
                  />
                </div>
              )}

              {/* Fog of War Overlay (MJ preview) */}
              {fogEnabled && isMJ && currentMap && (
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center'
                  }}
                >
                  <FogOverlay
                    width={currentMap.width || 1920}
                    height={currentMap.height || 1080}
                    revealedZones={fogRevealedZones}
                    enabled={fogEnabled}
                    settings={fogSettings}
                    className={fogMode ? "opacity-70" : "opacity-50"}
                  />
                </div>
              )}

              {/* Fog Brush Preview */}
              {fogMode && fogEnabled && currentMap && (
                <div className="absolute inset-0 pointer-events-none">
                  <FogBrushPreview
                    x={fogBrushPosition.x}
                    y={fogBrushPosition.y}
                    radius={fogBrushSize}
                    containerWidth={currentMap.width || 1920}
                    containerHeight={currentMap.height || 1080}
                    visible={fogBrushPosition.x !== null}
                  />
                </div>
              )}
              
              {/* Mode Indicator Banner */}
              {editMode && !fogMode && isMJ && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-orange-500 text-white shadow-lg animate-pulse">
                    <Edit3 className="h-3 w-3 mr-1" />
                    {language === 'fr' ? 'Mode Édition Actif' : 'Edit Mode Active'}
                  </Badge>
                </div>
              )}
              
              {/* Fog Mode Banner */}
              {fogMode && isMJ && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-purple-600 text-white shadow-lg animate-pulse">
                    <Cloud className="h-3 w-3 mr-1" />
                    {language === 'fr' ? 'Mode Brouillard' : 'Fog Mode'}
                  </Badge>
                </div>
              )}
              
              {/* Help Text */}
              <div className="absolute bottom-3 left-3 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-2 py-1 rounded">
                {fogMode ? (
                  language === 'fr' 
                    ? '🌫️ Cliquez et glissez pour révéler des zones • Changez la taille du pinceau ci-dessus'
                    : '🌫️ Click and drag to reveal areas • Change brush size above'
                ) : editMode && isMJ ? (
                  language === 'fr' 
                    ? '📌 Clic = nouveau pin • Glisser pin = déplacer • Double-clic pin = carte liée'
                    : '📌 Click = new pin • Drag pin = move • Double-click pin = linked map'
                ) : (
                  language === 'fr' 
                    ? '🗺️ Glisser = déplacer • Molette = zoom • Clic pin = info • Double-clic = carte liée'
                    : '🗺️ Drag = pan • Wheel = zoom • Click pin = info • Double-click = linked map'
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pin Detail Panel */}
        <Dialog open={pinDetailOpen} onOpenChange={setPinDetailOpen}>
          <DialogContent className="max-w-lg">
            {selectedPin && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: getPinColor(selectedPin.pin_type) }}
                    >
                      {React.createElement(getPinIcon(selectedPin.pin_type), { className: "h-5 w-5 text-white" })}
                    </div>
                    <div>
                      <DialogTitle className="font-cinzel">{selectedPin.title}</DialogTitle>
                      <DialogDescription>
                        {getPinLabel(selectedPin.pin_type, language)}
                        {!selectedPin.discovered && isMJ && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            <EyeOff className="h-3 w-3 mr-1" />
                            {language === 'fr' ? 'Non découvert' : 'Undiscovered'}
                          </Badge>
                        )}
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Description */}
                  {isMJ ? (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Nom' : 'Name'}</Label>
                        <Input
                          value={selectedPin.title}
                          onChange={(e) => setSelectedPin(prev => ({ ...prev, title: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Description publique' : 'Public description'}</Label>
                        <Textarea
                          value={selectedPin.public_description}
                          onChange={(e) => setSelectedPin(prev => ({ ...prev, public_description: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <EyeOff className="h-4 w-4" />
                          {language === 'fr' ? 'Notes MJ' : 'GM notes'}
                        </Label>
                        <Textarea
                          value={selectedPin.mj_notes}
                          onChange={(e) => setSelectedPin(prev => ({ ...prev, mj_notes: e.target.value }))}
                          rows={2}
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                      <p className="text-sm mt-1">{selectedPin.public_description || (language === 'fr' ? 'Aucune description' : 'No description')}</p>
                    </div>
                  )}

                  {/* Linked Location */}
                  {selectedPin.linked_locations?.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {language === 'fr' ? 'Lieu lié' : 'Linked location'}
                      </Label>
                      <div className="space-y-1">
                        {selectedPin.linked_locations.map(locId => {
                          const loc = getLinkedLocation(locId);
                          return loc ? (
                            <Badge key={locId} variant="secondary" className="mr-1">
                              {loc.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Linked Map - Navigate Button */}
                  {selectedPin.linked_map_id && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        {language === 'fr' ? 'Carte liée' : 'Linked map'}
                      </Label>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => navigateToMap(selectedPin.linked_map_id)}
                      >
                        <MapIcon className="h-4 w-4 mr-2" />
                        {getLinkedMap(selectedPin.linked_map_id)?.name || 'Map'}
                        <ChevronRight className="h-4 w-4 ml-auto" />
                      </Button>
                    </div>
                  )}

                  {/* MJ: Edit linked map */}
                  {isMJ && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link2 className="h-4 w-4" />
                        {language === 'fr' ? 'Lier à une carte' : 'Link to map'}
                      </Label>
                      <Select 
                        value={selectedPin.linked_map_id || 'none'} 
                        onValueChange={(v) => setSelectedPin(prev => ({ ...prev, linked_map_id: v === 'none' ? null : v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner une carte...' : 'Select a map...'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{language === 'fr' ? 'Aucune' : 'None'}</SelectItem>
                          {maps.filter(m => m.id !== currentMap?.id).map(map => (
                            <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* MJ: Link to location */}
                  {isMJ && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Compass className="h-4 w-4" />
                        {language === 'fr' ? 'Lier à un lieu' : 'Link to location'}
                      </Label>
                      <Select 
                        value={selectedPin.linked_locations?.[0] || 'none'} 
                        onValueChange={(v) => setSelectedPin(prev => ({ 
                          ...prev, 
                          linked_locations: v === 'none' ? [] : [v]
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'fr' ? 'Sélectionner un lieu...' : 'Select a location...'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{language === 'fr' ? 'Aucun' : 'None'}</SelectItem>
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Quick-create location from pin */}
                      {!creatingLocationFromPin ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => setCreatingLocationFromPin(true)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {language === 'fr' ? 'Créer un nouveau lieu' : 'Create new location'}
                        </Button>
                      ) : (
                        <div className="space-y-2 p-3 border rounded-lg bg-secondary/30">
                          <p className="text-xs font-medium text-muted-foreground">
                            {language === 'fr' ? 'Nouveau lieu' : 'New location'}
                          </p>
                          <Input
                            placeholder={language === 'fr' ? 'Nom du lieu...' : 'Location name...'}
                            value={newLocationFromPin.name}
                            onChange={(e) => setNewLocationFromPin(prev => ({ ...prev, name: e.target.value }))}
                            className="h-8 text-xs"
                            autoFocus
                          />
                          <Select
                            value={newLocationFromPin.location_type}
                            onValueChange={(v) => setNewLocationFromPin(prev => ({ ...prev, location_type: v }))}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder={language === 'fr' ? 'Type...' : 'Type...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                { id: 'city', label: { fr: 'Ville', en: 'City' } },
                                { id: 'dungeon', label: { fr: 'Donjon', en: 'Dungeon' } },
                                { id: 'building', label: { fr: 'Bâtiment', en: 'Building' } },
                                { id: 'wilderness', label: { fr: 'Nature', en: 'Wilderness' } },
                                { id: 'temple', label: { fr: 'Temple', en: 'Temple' } },
                                { id: 'camp', label: { fr: 'Campement', en: 'Camp' } },
                                { id: 'poi', label: { fr: 'Point d\'intérêt', en: 'Point of interest' } },
                              ].map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.label[language]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            placeholder={language === 'fr' ? 'Description...' : 'Description...'}
                            value={newLocationFromPin.public_description}
                            onChange={(e) => setNewLocationFromPin(prev => ({ ...prev, public_description: e.target.value }))}
                            rows={2}
                            className="text-xs"
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs flex-1"
                              onClick={() => { setCreatingLocationFromPin(false); setNewLocationFromPin({ name: '', location_type: 'poi', public_description: '' }); }}
                            >
                              {t('cancel', language)}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="text-xs flex-1"
                              disabled={!newLocationFromPin.name.trim() || savingNewLocation}
                              onClick={handleCreateLocationFromPin}
                            >
                              {savingNewLocation && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                              {t('create', language)}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex justify-between">
                  {isMJ && (
                    <div className="flex gap-2">
                      <Button variant="destructive" size="sm" onClick={handleDeletePin}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('delete', language)}
                      </Button>
                      {!selectedPin.discovered && (
                        <Button variant="outline" size="sm" onClick={handleRevealPin}>
                          <Eye className="h-4 w-4 mr-1" />
                          {language === 'fr' ? 'Révéler' : 'Reveal'}
                        </Button>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setPinDetailOpen(false)}>
                      {t('close', language)}
                    </Button>
                    {isMJ && (
                      <Button onClick={handleUpdatePin}>
                        {t('save', language)}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Map Dialog */}
        <Dialog open={createMapOpen} onOpenChange={setCreateMapOpen}>
          <DialogContent>
            <form onSubmit={handleCreateMap}>
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {language === 'fr' ? 'Nouvelle carte' : 'New map'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom' : 'Name'} *</Label>
                  <Input
                    placeholder={language === 'fr' ? 'Carte du monde' : 'World map'}
                    value={newMap.name}
                    onChange={(e) => setNewMap(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Image de la carte' : 'Map Image'} *</Label>

                  {/* Upload zone */}
                  <input
                    ref={createFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      handleUploadMapImage(e.target.files?.[0], 'create');
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => createFileRef.current?.click()}
                    disabled={uploadingImage}
                    className={cn(
                      'w-full border-2 border-dashed rounded-lg p-4 text-center transition-colors',
                      'hover:border-primary/50 hover:bg-primary/5',
                      newMap.image_url ? 'border-success/40 bg-success/5' : 'border-border',
                    )}
                  >
                    {uploadingImage ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {language === 'fr' ? 'Import en cours...' : 'Uploading...'}
                      </div>
                    ) : newMap.image_url && !newMap.image_url.startsWith('http') ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-success">
                        <Check className="h-4 w-4" />
                        {language === 'fr' ? 'Image importée' : 'Image uploaded'}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5">
                        <Upload className="h-6 w-6 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {language === 'fr' ? 'Cliquez pour importer une image' : 'Click to upload an image'}
                        </span>
                        <span className="text-[10px] text-muted-foreground/60">
                          JPG, PNG, WebP — max 50 Mo
                        </span>
                      </div>
                    )}
                  </button>

                  {/* Preview */}
                  {newMap.image_url && (
                    <div className="relative mt-2 rounded-lg overflow-hidden border border-border bg-muted/30 max-h-32">
                      <img
                        src={newMap.image_url}
                        alt="Preview"
                        className="w-full h-32 object-cover"
                        onLoad={(event) => setNewMap(prev => ({
                          ...prev,
                          width: event.currentTarget.naturalWidth || prev.width,
                          height: event.currentTarget.naturalHeight || prev.height,
                        }))}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setNewMap(prev => ({ ...prev, image_url: '' }))}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 flex items-center justify-center text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  {/* OR separator + URL fallback */}
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex-1 border-t border-border" />
                    {language === 'fr' ? 'ou coller une URL' : 'or paste a URL'}
                    <span className="flex-1 border-t border-border" />
                  </div>
                  <Input
                    placeholder="https://example.com/map.jpg"
                    value={newMap.image_url.startsWith('/assets') ? '' : newMap.image_url}
                    onChange={(e) => setNewMap(prev => ({ ...prev, image_url: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select value={newMap.map_type} onValueChange={(v) => setNewMap(prev => ({ ...prev, map_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAP_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label[language]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Carte parente' : 'Parent map'}</Label>
                  <Select 
                    value={newMap.parent_map_id || 'none'} 
                    onValueChange={(v) => setNewMap(prev => ({ ...prev, parent_map_id: v === 'none' ? null : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'fr' ? 'Aucune (racine)' : 'None (root)'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'fr' ? 'Aucune (racine)' : 'None (root)'}</SelectItem>
                      {maps.map(map => (
                        <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between p-3 border rounded-lg bg-secondary/30">
                  <div className="space-y-0.5">
                    <Label className="flex items-center gap-2">
                      {newMap.visibility === 'players' ? (
                        <Eye className="h-4 w-4 text-green-500" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      {language === 'fr' ? 'Visible par les joueurs' : 'Visible to players'}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? newMap.visibility === 'players' 
                          ? 'Les joueurs peuvent voir cette carte'
                          : 'Seul le MJ peut voir cette carte'
                        : newMap.visibility === 'players'
                          ? 'Players can see this map'
                          : 'Only GM can see this map'}
                    </p>
                  </div>
                  <Switch
                    checked={newMap.visibility === 'players'}
                    onCheckedChange={(checked) => setNewMap(prev => ({ 
                      ...prev, 
                      visibility: checked ? 'players' : 'mj_only' 
                    }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateMapOpen(false)}>
                  {t('cancel', language)}
                </Button>
                <Button type="submit" disabled={creating} className="fantasy-button">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('create', language)}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Map Dialog */}
        <Dialog open={editMapOpen} onOpenChange={(open) => { setEditMapOpen(open); if (!open) setEditingMap(null); }}>
          <DialogContent className="max-w-md">
            <form onSubmit={handleEditMap}>
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {language === 'fr' ? 'Modifier la carte' : 'Edit map'}
                </DialogTitle>
              </DialogHeader>
              {editingMap && (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Nom' : 'Name'} *</Label>
                    <Input
                      placeholder={language === 'fr' ? 'Carte du monde' : 'World map'}
                      value={editingMap.name}
                      onChange={(e) => setEditingMap(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Image de la carte' : 'Map Image'}</Label>

                    <input
                      ref={editFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        handleUploadMapImage(e.target.files?.[0], 'edit');
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => editFileRef.current?.click()}
                      disabled={uploadingEditImage}
                      className={cn(
                        'w-full border-2 border-dashed rounded-lg p-3 text-center transition-colors',
                        'hover:border-primary/50 hover:bg-primary/5',
                        'border-border',
                      )}
                    >
                      {uploadingEditImage ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {language === 'fr' ? 'Import en cours...' : 'Uploading...'}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Upload className="h-4 w-4" />
                          {language === 'fr' ? 'Importer une nouvelle image' : 'Upload new image'}
                          <span className="text-[10px] text-muted-foreground/60">max 50 Mo</span>
                        </div>
                      )}
                    </button>

                    {/* Current preview */}
                    {editingMap.image_url && (
                      <div className="rounded-lg overflow-hidden border border-border bg-muted/30 max-h-24 mt-1">
                        <img
                          src={editingMap.image_url}
                          alt="Current"
                          className="w-full h-24 object-cover"
                          onLoad={(event) => setEditingMap(prev => ({
                            ...prev,
                            width: event.currentTarget.naturalWidth || prev.width,
                            height: event.currentTarget.naturalHeight || prev.height,
                          }))}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span className="flex-1 border-t border-border" />
                      {language === 'fr' ? 'ou coller une URL' : 'or paste a URL'}
                      <span className="flex-1 border-t border-border" />
                    </div>
                    <Input
                      placeholder="https://example.com/map.jpg"
                      value={editingMap.image_url}
                      onChange={(e) => setEditingMap(prev => ({ ...prev, image_url: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Carte parente' : 'Parent map'}</Label>
                    <Select 
                      value={editingMap.parent_map_id || 'none'} 
                      onValueChange={(v) => setEditingMap(prev => ({ ...prev, parent_map_id: v === 'none' ? null : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'fr' ? 'Aucune (racine)' : 'None (root)'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{language === 'fr' ? 'Aucune (racine)' : 'None (root)'}</SelectItem>
                        {maps.filter(m => m.id !== editingMap.id).map(map => (
                          <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Visibility Toggle */}
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-secondary/30">
                    <div className="space-y-0.5">
                      <Label className="flex items-center gap-2">
                        {editingMap.visibility === 'players' ? (
                          <Eye className="h-4 w-4 text-green-500" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        {language === 'fr' ? 'Visible par les joueurs' : 'Visible to players'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' 
                          ? editingMap.visibility === 'players' 
                            ? 'Les joueurs peuvent voir cette carte'
                            : 'Seul le MJ peut voir cette carte'
                          : editingMap.visibility === 'players'
                            ? 'Players can see this map'
                            : 'Only GM can see this map'}
                      </p>
                    </div>
                    <Switch
                      checked={editingMap.visibility === 'players'}
                      onCheckedChange={(checked) => setEditingMap(prev => ({ 
                        ...prev, 
                        visibility: checked ? 'players' : 'mj_only' 
                      }))}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setEditMapOpen(false); setEditingMap(null); }}>
                  {t('cancel', language)}
                </Button>
                <Button type="submit" disabled={creating} className="fantasy-button">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Pin Dialog */}
        <Dialog open={createPinOpen} onOpenChange={(open) => { setCreatePinOpen(open); if (!open) setClickPosition(null); }}>
          <DialogContent className="max-w-lg">
            <form onSubmit={handleCreatePin}>
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {language === 'fr' ? 'Nouveau point d\'intérêt' : 'New point of interest'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom' : 'Name'} *</Label>
                  <Input
                    placeholder={language === 'fr' ? 'Château de Ravenloft' : 'Castle Ravenloft'}
                    value={newPin.title}
                    onChange={(e) => setNewPin(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {PIN_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <Button
                          key={type.id}
                          type="button"
                          variant={newPin.pin_type === type.id ? 'default' : 'outline'}
                          className="flex-col h-auto py-2"
                          onClick={() => setNewPin(prev => ({ ...prev, pin_type: type.id }))}
                          style={newPin.pin_type === type.id ? { backgroundColor: type.color } : {}}
                        >
                          <Icon className="h-4 w-4 mb-1" />
                          <span className="text-xs">{type.label[language]}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Lier à un lieu existant' : 'Link to existing location'}</Label>
                  <Select 
                    value={newPin.linked_location_id || 'none'} 
                    onValueChange={(v) => setNewPin(prev => ({ ...prev, linked_location_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'fr' ? 'Aucun' : 'None'}</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Ouvre une autre carte' : 'Opens another map'}</Label>
                  <Select 
                    value={newPin.linked_map_id || 'none'} 
                    onValueChange={(v) => setNewPin(prev => ({ ...prev, linked_map_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{language === 'fr' ? 'Aucune' : 'None'}</SelectItem>
                      {maps.filter(m => m.id !== currentMap?.id).map(map => (
                        <SelectItem key={map.id} value={map.id}>
                          <span className="flex items-center gap-2">
                            <Layers className="h-3 w-3" />
                            {map.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Double-cliquez sur le pin pour naviguer vers cette carte'
                      : 'Double-click the pin to navigate to this map'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Description publique' : 'Public description'}</Label>
                  <Textarea
                    value={newPin.public_description}
                    onChange={(e) => setNewPin(prev => ({ ...prev, public_description: e.target.value }))}
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Notes MJ' : 'GM notes'}</Label>
                  <Textarea
                    value={newPin.mj_notes}
                    onChange={(e) => setNewPin(prev => ({ ...prev, mj_notes: e.target.value }))}
                    rows={2}
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newPin.discovered}
                    onCheckedChange={(v) => setNewPin(prev => ({ ...prev, discovered: v, visibility: v ? 'players' : 'mj_only' }))}
                  />
                  <Label>{language === 'fr' ? 'Visible par les joueurs' : 'Visible to players'}</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setCreatePinOpen(false); setClickPosition(null); }}>
                  {t('cancel', language)}
                </Button>
                <Button type="submit" disabled={creating} className="fantasy-button">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('create', language)}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Player View Dialog */}
        <Dialog open={playerViewDialogOpen} onOpenChange={setPlayerViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-cinzel flex items-center gap-2">
                <MonitorPlay className="h-5 w-5" />
                {language === 'fr' ? 'Vue Joueur' : 'Player View'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Générez un lien unique puis changez la carte affichée à tout moment'
                  : 'Generate one link, then switch the displayed map anytime'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Token/Link Section */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">
                  {language === 'fr' ? 'Lien public' : 'Public Link'}
                </Label>
                
                {playerViewToken ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input 
                        readOnly 
                        value={`${window.location.origin}/player-view/map/${playerViewToken}`}
                        className="text-xs"
                      />
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={copyPlayerViewUrl}
                      >
                        {tokenCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={openPlayerView} className="flex-1 gap-2">
                        <ExternalLink className="h-4 w-4" />
                        {language === 'fr' ? 'Ouvrir la vue' : 'Open View'}
                      </Button>
                      <Button variant="outline" onClick={setCurrentMapForPlayerView} title={language === 'fr' ? 'Afficher cette carte dans la vue joueur' : 'Show this map in player view'}>
                        <MonitorPlay className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={generatePlayerViewToken} className="w-full gap-2">
                    <Link2 className="h-4 w-4" />
                    {language === 'fr' ? 'Générer un lien' : 'Generate Link'}
                  </Button>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {language === 'fr'
                    ? 'Le lien reste le même pour toute la campagne'
                    : 'This link stays the same for the whole campaign'}
                </p>
                {playerViewToken && (
                  <div className="rounded-lg border border-border/80 bg-secondary/20 p-3 space-y-2">
                    {activePlayerMapId ? (
                      <div className="flex items-center justify-between">
                        <p className="text-sm">
                          {language === 'fr' ? 'Carte affichée :' : 'Displayed map:'}{' '}
                          <strong>{activePlayerMapName || activePlayerMapId}</strong>
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive h-7 px-2"
                          onClick={clearPlayerViewDisplay}
                        >
                          <ImageOff className="h-3.5 w-3.5 mr-1" />
                          {language === 'fr' ? 'Retirer' : 'Clear'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Aucune carte affichée actuellement' : 'No map currently displayed'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Sync Section */}
              {playerViewToken && (
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <RefreshCw className={cn("h-4 w-4", syncEnabled && "animate-spin text-green-500")} />
                    {language === 'fr' ? 'Synchronisation MJ' : 'GM Sync'}
                  </Label>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">
                        {language === 'fr'
                          ? 'Contrôler la vue des joueurs'
                          : 'Control player view'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr'
                          ? 'La position et le zoom suivront votre vue'
                          : 'Position and zoom will follow your view'}
                      </p>
                    </div>
                    <Switch
                      checked={syncEnabled}
                      onCheckedChange={toggleSync}
                    />
                  </div>

                  {syncEnabled && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <p className="text-sm text-green-500 flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        {language === 'fr'
                          ? 'Synchronisation active - Les joueurs suivent votre vue'
                          : 'Sync active - Players follow your view'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Default Image Section */}
              {playerViewToken && (
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    {language === 'fr' ? 'Écran d\'attente' : 'Idle Screen'}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr'
                      ? 'Image affichée quand aucune carte n\'est sélectionnée (ex: illustration de campagne)'
                      : 'Image shown when no map is selected (e.g. campaign artwork)'}
                  </p>

                  {playerViewDefaultImage ? (
                    <div className="space-y-2">
                      <div className="relative rounded-lg overflow-hidden border border-border aspect-video bg-secondary/30">
                        <img
                          src={playerViewDefaultImage}
                          alt="Default"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => document.getElementById('pv-default-img-input').click()}
                          disabled={uploadingDefaultImage}
                        >
                          {uploadingDefaultImage ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                          {language === 'fr' ? 'Remplacer' : 'Replace'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={removePlayerViewDefaultImage}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          {language === 'fr' ? 'Supprimer' : 'Remove'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => document.getElementById('pv-default-img-input').click()}
                      disabled={uploadingDefaultImage}
                    >
                      {uploadingDefaultImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {language === 'fr' ? 'Choisir une image' : 'Choose an image'}
                    </Button>
                  )}
                  <input
                    id="pv-default-img-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadPlayerViewDefaultImage(file);
                      e.target.value = '';
                    }}
                  />
                </div>
              )}

              {/* Instructions */}
              <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm">
                  {language === 'fr' ? 'Conseils d\'utilisation' : 'Usage Tips'}
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• {language === 'fr' ? 'Ouvrez la vue joueur sur un écran TV ou projecteur' : 'Open player view on a TV or projector'}</li>
                  <li>• {language === 'fr' ? 'Activez la synchronisation pour guider vos joueurs' : 'Enable sync to guide your players'}</li>
                  <li>• {language === 'fr' ? 'Seuls les pins révélés sont visibles' : 'Only revealed pins are visible'}</li>
                  <li>• {language === 'fr' ? '"Retirer" masque la carte et affiche l\'écran d\'attente' : '"Clear" hides the map and shows the idle screen'}</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPlayerViewDialogOpen(false)}>
                {t('close', language)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}

// Recursive component for map tree
function MapTreeItem({ map, maps, currentMap, language, onSelect, onEdit, isMJ, depth = 0 }) {
  const [expanded, setExpanded] = useState(true);
  const children = maps.filter(m => m.parent_map_id === map.id);
  const hasChildren = children.length > 0;
  const isHidden = map.visibility === 'mj_only';
  
  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-secondary/50 text-sm group",
          currentMap?.id === map.id && "bg-secondary"
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => onSelect(map)}
      >
        {hasChildren ? (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-5 w-5 p-0"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronLeft className="h-3 w-3 rotate-[-90deg]" /> : <ChevronRight className="h-3 w-3" />}
          </Button>
        ) : (
          <div className="w-5" />
        )}
        <MapIcon className="h-4 w-4 text-muted-foreground" />
        <span className={cn("truncate flex-1", isHidden && "text-muted-foreground italic")}>{map.name}</span>
        
        {/* Hidden indicator */}
        {isHidden && isMJ && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <EyeOff className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top">
                {language === 'fr' ? 'Cachée aux joueurs' : 'Hidden from players'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {/* Edit button (MJ only) */}
        {isMJ && onEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onEdit(map); }}
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        )}
      </div>
      
      {hasChildren && expanded && (
        <div>
          {children.map(child => (
            <MapTreeItem
              key={child.id}
              map={child}
              maps={maps}
              currentMap={currentMap}
              language={language}
              onSelect={onSelect}
              onEdit={onEdit}
              isMJ={isMJ}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
